import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { fmtDate } from "../utils";
import { Card, Badge, SectionTitle, EmptyState, RefreshBtn } from "./primitives";

type Metrics = {
  active_requests: { in_legal_review: number; awaiting_business: number; in_negotiation: number; awaiting_signature: number };
  avg_cycle_time_30d: number | null;
  requests_by_business_unit: { business_unit: string; count: number }[];
  lawyer_workload: { lawyer_id: string; lawyer_name: string; assigned_count: number }[];
  recently_executed: { id: string; title: string; counterparty: string; executed_date: string; business_unit: string | null }[];
  upcoming_renewals: { id: string; title: string; counterparty: string; renewal_date: string }[];
  sla_summary?: { breached_count: number; at_risk_count: number; on_track_count: number };
  sla_breaches?: { id: string; title: string; counterparty: string; business_unit: string | null; sla_target_date: string; sla_severity: string | null; days_overdue: number }[];
  spend_summary?: {
    current_fiscal_year: number;
    total_budget: number;
    total_spent: number;
    percent_used: number | null;
    by_business_unit: { business_unit: string; budget: number; spent: number; percent_used: number | null }[];
    has_budgets: boolean;
  };
};

const CARD_COLORS: { key: keyof Metrics["active_requests"]; label: string; accent: string; bg: string }[] = [
  { key: "in_legal_review", label: "In Legal Review", accent: T.ylw, bg: T.ylwS },
  { key: "awaiting_business", label: "Awaiting Business", accent: T.acc, bg: T.accS },
  { key: "in_negotiation", label: "In Negotiation", accent: "#8e6fd6", bg: "rgba(142,111,214,0.12)" },
  { key: "awaiting_signature", label: "Awaiting Signature", accent: T.grn, bg: T.grnS },
];

export const InHouseDashboard = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [workloadSort, setWorkloadSort] = useState<"count" | "name">("count");
  const [rollup, setRollup] = useState(false);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const data = await apiCall(`/dashboard/in-house-metrics${rollup ? "?rollup=true" : ""}`);
    if (data && data.active_requests) {
      setMetrics(data);
      setLastRefresh(new Date());
    }
    setLoading(false);
  }, [rollup]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { const i = setInterval(() => load(false), 30000); return () => clearInterval(i); }, [load]);

  if (loading && !metrics) {
    return <div style={{ padding: 40, textAlign: "center", color: T.txM, fontSize: 12 }}>Loading legal team dashboard...</div>;
  }
  if (!metrics) {
    return <EmptyState icon="◈" msg="Dashboard metrics unavailable" />;
  }

  const maxBuCount = Math.max(1, ...metrics.requests_by_business_unit.map(r => r.count));
  const workload = [...metrics.lawyer_workload].sort((a, b) =>
    workloadSort === "count" ? b.assigned_count - a.assigned_count : a.lawyer_name.localeCompare(b.lawyer_name)
  );

  return (
    <div>
      <SectionTitle
        sub={lastRefresh ? `Last refreshed ${lastRefresh.toLocaleTimeString()}` : ""}
        right={<RefreshBtn onClick={() => load(false)} />}
      >Legal Team Dashboard</SectionTitle>

      {/* 4-up active request cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {CARD_COLORS.map(c => (
          <Card key={c.key} style={{ padding: 16, borderLeft: `3px solid ${c.accent}`, backgroundColor: c.bg }}>
            <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: T.tx, lineHeight: 1 }}>{metrics.active_requests[c.key]}</div>
          </Card>
        ))}
      </div>

      {/* Cycle time + BU chart */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 12, marginBottom: 16 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>Avg Cycle Time (30d)</div>
          <div style={{ fontSize: 40, fontWeight: 800, color: T.tx, lineHeight: 1 }}>
            {metrics.avg_cycle_time_30d == null ? "—" : metrics.avg_cycle_time_30d.toFixed(1)}
            {metrics.avg_cycle_time_30d != null && <span style={{ fontSize: 14, color: T.txM, fontWeight: 500, marginLeft: 6 }}>days</span>}
          </div>
          <div style={{ fontSize: 11, color: T.txM, marginTop: 8 }}>From request submission to contract execution, last 30 days.</div>
        </Card>
        <Card style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>Requests by Business Unit</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setRollup(false)} style={{ padding: "3px 8px", borderRadius: 3, fontSize: 10, border: `1px solid ${T.brd}`, backgroundColor: !rollup ? T.bg3 : "transparent", color: !rollup ? T.tx : T.txM, cursor: "pointer", fontFamily: "inherit" }}>Leaf only</button>
              <button onClick={() => setRollup(true)} style={{ padding: "3px 8px", borderRadius: 3, fontSize: 10, border: `1px solid ${T.brd}`, backgroundColor: rollup ? T.bg3 : "transparent", color: rollup ? T.tx : T.txM, cursor: "pointer", fontFamily: "inherit" }}>Rolled up</button>
            </div>
          </div>
          {metrics.requests_by_business_unit.length === 0 ? (
            <EmptyState icon="◈" msg="No requests yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {metrics.requests_by_business_unit.map(r => (
                <div key={r.business_unit} style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 12, color: T.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.business_unit}</div>
                  <div style={{ height: 18, backgroundColor: T.bg3, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${(r.count / maxBuCount) * 100}%`, height: "100%", backgroundColor: T.acc }} />
                  </div>
                  <div style={{ fontSize: 12, color: T.tx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.count}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Lawyer workload */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>Lawyer Workload</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setWorkloadSort("count")} style={{ padding: "4px 10px", borderRadius: 4, fontSize: 11, border: `1px solid ${T.brd}`, backgroundColor: workloadSort === "count" ? T.bg3 : "transparent", color: workloadSort === "count" ? T.tx : T.txM, cursor: "pointer", fontFamily: "inherit" }}>By count</button>
            <button onClick={() => setWorkloadSort("name")} style={{ padding: "4px 10px", borderRadius: 4, fontSize: 11, border: `1px solid ${T.brd}`, backgroundColor: workloadSort === "name" ? T.bg3 : "transparent", color: workloadSort === "name" ? T.tx : T.txM, cursor: "pointer", fontFamily: "inherit" }}>By name</button>
          </div>
        </div>
        {workload.length === 0 ? <EmptyState icon="◈" msg="No active lawyer assignments" /> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: "left", color: T.txD, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em" }}>
                <th style={{ padding: "6px 8px", borderBottom: `1px solid ${T.brd}` }}>Lawyer</th>
                <th style={{ padding: "6px 8px", borderBottom: `1px solid ${T.brd}`, textAlign: "right", width: 140 }}>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {workload.map(w => (
                <tr key={w.lawyer_id}>
                  <td style={{ padding: "8px", borderBottom: `1px solid ${T.brd}`, color: T.tx }}>{w.lawyer_name}</td>
                  <td style={{ padding: "8px", borderBottom: `1px solid ${T.brd}`, textAlign: "right", color: T.tx, fontVariantNumeric: "tabular-nums" }}>{w.assigned_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* SLA status */}
      {metrics.sla_summary && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
            <Card style={{ padding: 16, borderLeft: `3px solid ${T.red}`, backgroundColor: metrics.sla_summary.breached_count > 0 ? "rgba(214,77,77,.08)" : undefined }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>SLA Breached</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: metrics.sla_summary.breached_count > 0 ? T.red : T.tx, lineHeight: 1 }}>{metrics.sla_summary.breached_count}</div>
            </Card>
            <Card style={{ padding: 16, borderLeft: `3px solid ${T.ylw}` }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>At Risk (≤3d)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.tx, lineHeight: 1 }}>{metrics.sla_summary.at_risk_count}</div>
            </Card>
            <Card style={{ padding: 16, borderLeft: `3px solid ${T.grn}` }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>On Track</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.tx, lineHeight: 1 }}>{metrics.sla_summary.on_track_count}</div>
            </Card>
          </div>
          {metrics.sla_breaches && metrics.sla_breaches.length > 0 && (
            <Card style={{ padding: 16, marginBottom: 16, borderLeft: `3px solid ${T.red}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 10 }}>SLA Breaches ({metrics.sla_breaches.length} shown)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {metrics.sla_breaches.map(b => (
                  <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 8px", borderBottom: `1px solid ${T.brd}` }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, color: T.tx, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</div>
                      <div style={{ fontSize: 11, color: T.txM }}>{b.counterparty}{b.business_unit ? ` • ${b.business_unit}` : ""}</div>
                    </div>
                    {b.sla_severity && <Badge c={b.sla_severity === "critical" ? T.red : b.sla_severity === "high" ? T.ylw : T.acc} b={T.bg3}>{b.sla_severity}</Badge>}
                    <div style={{ fontSize: 11, color: T.red, width: 110, textAlign: "right", fontWeight: 600 }}>{b.days_overdue}d overdue</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Spend status (FY budget vs actual) */}
      {metrics.spend_summary && (
        <>
          {metrics.spend_summary.has_budgets ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
                <Card style={{ padding: 16, borderLeft: `3px solid ${T.acc}` }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>FY{metrics.spend_summary.current_fiscal_year} Budget</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.tx, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>${metrics.spend_summary.total_budget.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                </Card>
                <Card style={{ padding: 16, borderLeft: `3px solid ${T.ylw}` }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>FY{metrics.spend_summary.current_fiscal_year} Spent</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.tx, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>${metrics.spend_summary.total_spent.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                </Card>
                <Card style={{ padding: 16, borderLeft: `3px solid ${metrics.spend_summary.percent_used != null && metrics.spend_summary.percent_used >= 100 ? T.red : metrics.spend_summary.percent_used != null && metrics.spend_summary.percent_used >= 80 ? T.ylw : T.grn}` }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700, marginBottom: 6 }}>% Used</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: metrics.spend_summary.percent_used != null && metrics.spend_summary.percent_used >= 100 ? T.red : T.tx, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{metrics.spend_summary.percent_used == null ? "—" : `${metrics.spend_summary.percent_used}%`}</div>
                </Card>
              </div>
              {metrics.spend_summary.by_business_unit.length > 0 && (
                <Card style={{ padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Top {metrics.spend_summary.by_business_unit.length} BUs by % Used</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {metrics.spend_summary.by_business_unit.map(r => {
                      const pct = r.percent_used ?? 0;
                      const barColor = pct >= 100 ? T.red : pct >= 80 ? T.ylw : T.grn;
                      const maxBudget = Math.max(1, ...metrics.spend_summary!.by_business_unit.map(x => x.budget));
                      const budgetPctOfMax = (r.budget / maxBudget) * 100;
                      const spentPctOfMax = (r.spent / maxBudget) * 100;
                      return (
                        <div key={r.business_unit} style={{ display: "grid", gridTemplateColumns: "160px 1fr 120px 60px", gap: 10, alignItems: "center" }}>
                          <div style={{ fontSize: 12, color: T.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.business_unit || "(unknown)"}</div>
                          <div style={{ position: "relative", height: 18, backgroundColor: T.bg3, borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ position: "absolute", left: 0, top: 0, width: `${budgetPctOfMax}%`, height: "100%", backgroundColor: T.bg4 }} />
                            <div style={{ position: "absolute", left: 0, top: 0, width: `${Math.min(spentPctOfMax, budgetPctOfMax)}%`, height: "100%", backgroundColor: barColor, opacity: 0.85 }} />
                          </div>
                          <div style={{ fontSize: 11, color: T.txM, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>${r.spent.toLocaleString("en-US", { maximumFractionDigits: 0 })} / ${r.budget.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                          <div style={{ fontSize: 12, color: barColor, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.percent_used == null ? "—" : `${r.percent_used}%`}</div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </>
          ) : (
            <Card style={{ padding: 16, marginBottom: 16, borderLeft: `3px solid ${T.txD}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Spend Status</div>
              <div style={{ fontSize: 11, color: T.txM }}>No budgets configured. Set up budgets in Admin &gt; Workflow Config &gt; Budgets.</div>
            </Card>
          )}
        </>
      )}

      {/* Recently executed */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Recently Executed</div>
        {metrics.recently_executed.length === 0 ? <EmptyState icon="◈" msg="No recent executions" /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {metrics.recently_executed.map(r => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 8px", borderBottom: `1px solid ${T.brd}` }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, color: T.tx, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{r.counterparty}</div>
                </div>
                {r.business_unit && <Badge c={T.txM} b={T.bg3}>{r.business_unit}</Badge>}
                <div style={{ fontSize: 11, color: T.txD, width: 90, textAlign: "right" }}>{fmtDate(r.executed_date)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Upcoming renewals */}
      {metrics.upcoming_renewals.length > 0 && (
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Renewals in next 90 days</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {metrics.upcoming_renewals.map(r => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 8px", borderBottom: `1px solid ${T.brd}` }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, color: T.tx, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{r.counterparty}</div>
                </div>
                <div style={{ fontSize: 11, color: T.txD, width: 90, textAlign: "right" }}>{fmtDate(r.renewal_date)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
